import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Input } from '@/app/components/form/Input';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import { useCreateClientPassportData } from '../../hooks/useCreateClientPassportData';
import { useUpdateClientPassportData } from '../../hooks/useUpdateClientPassportData';

interface PrimaryTravelDataProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const PrimaryTravelData = ({ onUpdateFormData }: PrimaryTravelDataProps) => {
  const basePath = `primary_passenger.tsa`;
  const passportPath = `primary_passenger.passport`;

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const primaryPassengerId = watch('primary_passenger.client.client_id');
  const clientId = primaryPassengerId ? String(primaryPassengerId) : '';

  const { mutateAsync: createPassport } = useCreateClientPassportData();
  const { mutateAsync: updatePassport } = useUpdateClientPassportData();

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  const passportId = watch(`${passportPath}.passport_id`) as string | null;

  const savePassport = async (passportNumber: string | null, expiryDate: string | null) => {
    if (!clientId) return;
    const expiryYYYYMMDD = expiryDate ? formatMMDDYYYYToYYYYMMDD(expiryDate) : null;
    try {
      if (passportId) {
        await updatePassport({
          passportId,
          data: { passport_number: passportNumber ?? null, expiry_date: expiryYYYYMMDD ?? null },
        });
      } else {
        if (!passportNumber?.trim() && !expiryYYYYMMDD) return;
        await createPassport(
          {
            clientId,
            data: { passport_number: passportNumber?.trim() ?? '', expiry_date: expiryYYYYMMDD ?? '' },
          },
          {
            onSuccess: (data) => {
              setValue(`${passportPath}.passport_id`, data.data.id);
              setValue(`${passportPath}.passport_number`, data.data.passport_number ?? null);
              setValue(`${passportPath}.expiry_date`, data.data.expiry_date ?? null);
            },
          }
        );
      }
    } catch {
      toast.error('Failed to save passport data');
    }
  };

  const handlePassportNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value?.trim() || null;
    const expiry = watch(`${passportPath}.expiry_date`) as string | null | undefined;
    savePassport(value, expiry ?? null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <Input
        {...register(`${basePath}.global_entry`)}
        intent="primaryWhite"
        label="Global entry number"
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Controller
        name={`${basePath}.global_expiry_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${basePath}.global_expiry_date`}
            label="Global entry expiry date"
            minDate={new Date()}
            intent="primaryWhite"
            errors={errors}
            value={field.value || ''}
            onChangeDate={(date) => {
              field.onChange(date);
              onUpdateFormData?.(`${basePath}.global_expiry_date`, formatMMDDYYYYToYYYYMMDD(date));
            }}
          />
        )}
      />

      <Input
        {...register(`${basePath}.known_traveler_number`)}
        intent="primaryWhite"
        label="Known traveler number (KTN)"
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Controller
        name={`${basePath}.known_traveler_number_expire_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${basePath}.known_traveler_number_expire_date`}
            label="KTN expiry date"
            minDate={new Date()}
            intent="primaryWhite"
            errors={errors}
            value={(field.value as string) || ''}
            onChangeDate={(date) => {
              field.onChange(date);
              onUpdateFormData?.(`${basePath}.known_traveler_number_expire_date`, formatMMDDYYYYToYYYYMMDD(date));
            }}
          />
        )}
      />

      <Input
        {...register(`${passportPath}.passport_number`)}
        value={(watch(`${passportPath}.passport_number`) as string) ?? ''}
        intent="primaryWhite"
        label="Passport number"
        onBlur={handlePassportNumberBlur}
      />

      <Controller
        name={`${passportPath}.expiry_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${passportPath}.expiry_date`}
            label="Passport expiry date"
            minDate={new Date()}
            intent="primaryWhite"
            errors={errors}
            value={(field.value as string) || ''}
            onChangeDate={(dateStr) => {
              field.onChange(dateStr);
              savePassport((watch(`${passportPath}.passport_number`) as string) ?? null, dateStr);
            }}
          />
        )}
      />
    </div>
  );
};
